import { normalizeAmenityOrderDetails } from '@/features/amenities/utils/normalize-amenities';
import type { PaginatedResponse } from '@/shared/types/api.types';

import type {
  BookingCustomer,
  BookingHistoryItem,
  BookingPaymentRequirement,
  BookingPaymentStatus,
  BookingTimeSlot,
  Pro20Page,
} from '../types/booking.types';

const PAYMENT_REQUIREMENTS = new Set<BookingPaymentRequirement>([
  'NONE',
  'CARD_VERIFICATION',
  'PAYMENT',
  'DEPOSIT',
]);

const PAYMENT_STATUSES = new Set<BookingPaymentStatus>([
  'NOT_REQUIRED',
  'PENDING',
  'AWAITING_STRIPE_CONFIRMATION',
  'CARD_VERIFIED',
  'AUTHORIZED',
  'PAID',
  'PARTIALLY_PAID',
  'FAILED',
  'CANCELED',
  'REFUND_PENDING',
  'PARTIALLY_REFUNDED',
  'REFUNDED',
  'REFUND_FAILED',
]);

function parsePaymentRequirement(
  value: unknown,
): BookingPaymentRequirement | undefined {
  return typeof value === 'string' &&
    PAYMENT_REQUIREMENTS.has(value as BookingPaymentRequirement)
    ? (value as BookingPaymentRequirement)
    : undefined;
}

function parsePaymentStatus(value: unknown): BookingPaymentStatus | undefined {
  return typeof value === 'string' &&
    PAYMENT_STATUSES.has(value as BookingPaymentStatus)
    ? (value as BookingPaymentStatus)
    : undefined;
}

function isPro20Page<T>(value: unknown): value is Pro20Page<T> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'content' in value &&
    Array.isArray((value as Pro20Page<T>).content)
  );
}

function parseNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function parseGeneratedBookingScore(
  raw: Record<string, unknown>,
): number | null {
  const scoreKeys = [
    'generatedBookingScore',
    'bookingGenerationScore',
    'generationScore',
    'bookingScore',
    'score',
  ] as const;

  for (const key of scoreKeys) {
    const score = parseNumber(raw[key]);
    if (score !== null) {
      return Math.min(100, Math.max(0, Math.round(score)));
    }
  }

  return null;
}

function normalizeBookingCustomer(raw: unknown): BookingCustomer {
  if (!raw || typeof raw !== 'object') {
    return {
      customerId: 0,
      customerCode: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      prefix: '',
    };
  }

  const customer = raw as Record<string, unknown>;

  return {
    customerId: parseNumber(customer.customerId) ?? 0,
    customerCode:
      typeof customer.customerCode === 'string' ? customer.customerCode : '',
    firstName: typeof customer.firstName === 'string' ? customer.firstName : '',
    lastName: typeof customer.lastName === 'string' ? customer.lastName : '',
    email: typeof customer.email === 'string' ? customer.email : '',
    phone: typeof customer.phone === 'string' ? customer.phone : '',
    prefix: typeof customer.prefix === 'string' ? customer.prefix : '',
    notes: typeof customer.notes === 'string' ? customer.notes : null,
    tagsList: Array.isArray(customer.tagsList)
      ? customer.tagsList.filter(
          (tag): tag is string => typeof tag === 'string',
        )
      : undefined,
    imageUrl: typeof customer.imageUrl === 'string' ? customer.imageUrl : null,
    isWalkInCustomer:
      typeof customer.isWalkInCustomer === 'boolean'
        ? customer.isWalkInCustomer
        : undefined,
    flames: parseNumber(customer.flames),
    hasCreditCardDetails:
      typeof customer.hasCreditCardDetails === 'boolean'
        ? customer.hasCreditCardDetails
        : undefined,
    isInBlacklist:
      typeof customer.isInBlacklist === 'boolean'
        ? customer.isInBlacklist
        : undefined,
  };
}

function normalizeTimeSlot(item: Record<string, unknown>): BookingTimeSlot {
  const nested =
    item.timeSlot && typeof item.timeSlot === 'object'
      ? (item.timeSlot as Record<string, unknown>)
      : null;

  return {
    timeRangeCode:
      typeof nested?.timeRangeCode === 'string'
        ? nested.timeRangeCode
        : typeof item.timeRangeCode === 'string'
          ? item.timeRangeCode
          : null,
    bookingHour:
      parseNumber(nested?.bookingHour) ?? parseNumber(item.bookingHour) ?? 0,
    bookingMinutes:
      parseNumber(nested?.bookingMinutes) ??
      parseNumber(item.bookingMinutes) ??
      0,
  };
}

function normalizeBookingHistoryItem(raw: unknown): BookingHistoryItem {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid booking history item');
  }

  const item = raw as Record<string, unknown>;
  const customer = normalizeBookingCustomer(item.customer);
  const generatedBookingScore = parseGeneratedBookingScore(item);
  const timeSlot = normalizeTimeSlot(item);

  return {
    ...(item as unknown as BookingHistoryItem),
    bookingId: parseNumber(item.bookingId) ?? 0,
    bookingCode: typeof item.bookingCode === 'string' ? item.bookingCode : '',
    branchCode: typeof item.branchCode === 'string' ? item.branchCode : '',
    branchName: typeof item.branchName === 'string' ? item.branchName : '',
    bookingDate: typeof item.bookingDate === 'string' ? item.bookingDate : '',
    bookingDateEnd:
      typeof item.bookingDateEnd === 'string' ? item.bookingDateEnd : null,
    timeSlot,
    numGuests: parseNumber(item.numGuests) ?? 0,
    numChildren: parseNumber(item.numChildren) ?? 0,
    status: typeof item.status === 'string' ? item.status : '',
    customer,
    createdAt: typeof item.createdAt === 'string' ? item.createdAt : '',
    bookingSource:
      typeof item.bookingSource === 'string' ? item.bookingSource : '',
    privateNotes:
      typeof item.privateNotes === 'string' ? item.privateNotes : '',
    specialRequests:
      typeof item.specialRequests === 'string' ? item.specialRequests : '',
    processedBy: typeof item.processedBy === 'string' ? item.processedBy : '',
    noShow: item.noShow === true,
    imageUrl: typeof item.imageUrl === 'string' ? item.imageUrl : null,
    pdfUrl: typeof item.pdfUrl === 'string' ? item.pdfUrl : null,
    generatedBookingScore,
    formCode: typeof item.formCode === 'string' ? item.formCode : null,
    amenityOrderDetailsDTO: item.amenityOrderDetailsDTO
      ? normalizeAmenityOrderDetails(item.amenityOrderDetailsDTO)
      : null,
    bookingCommunicationLastOutcome:
      typeof item.bookingCommunicationLastOutcome === 'string'
        ? item.bookingCommunicationLastOutcome
        : null,
    involvedCustomerInMessageLogic:
      typeof item.involvedCustomerInMessageLogic === 'boolean'
        ? item.involvedCustomerInMessageLogic
        : undefined,
    isReminderMessageSend:
      typeof item.isReminderMessageSend === 'boolean'
        ? item.isReminderMessageSend
        : undefined,
    paymentRequirement: parsePaymentRequirement(item.paymentRequirement),
    paymentStatus: parsePaymentStatus(item.paymentStatus),
    paymentCurrency:
      typeof item.paymentCurrency === 'string'
        ? item.paymentCurrency
        : undefined,
    paymentRequestedAmountMinorUnits:
      parseNumber(item.paymentRequestedAmountMinorUnits) ?? undefined,
    depositPaidAmountMinorUnits:
      parseNumber(item.depositPaidAmountMinorUnits) ?? undefined,
    depositAuthorizedAmountMinorUnits:
      parseNumber(item.depositAuthorizedAmountMinorUnits) ?? undefined,
    pendingPaymentOperationsCount:
      parseNumber(item.pendingPaymentOperationsCount) ?? undefined,
  };
}

function normalizeItems(items: unknown[]): BookingHistoryItem[] {
  return items.map((item) => normalizeBookingHistoryItem(item));
}

/** Normalizes PRO20 booking history results into a paginated shape for the UI */
export function normalizeBookingHistoryResponse(
  results:
    | Pro20Page<BookingHistoryItem>
    | BookingHistoryItem[]
    | BookingHistoryItem,
  fallbackSize = 20,
): PaginatedResponse<BookingHistoryItem> {
  if (isPro20Page<BookingHistoryItem>(results)) {
    return {
      data: normalizeItems(results.content),
      meta: {
        page: results.number + 1,
        limit: results.size,
        total: results.totalElements,
        totalPages: Math.max(results.totalPages, 1),
      },
    };
  }

  if (Array.isArray(results)) {
    return {
      data: normalizeItems(results),
      meta: {
        page: 1,
        limit: results.length || fallbackSize,
        total: results.length,
        totalPages: 1,
      },
    };
  }

  return {
    data: [normalizeBookingHistoryItem(results)],
    meta: {
      page: 1,
      limit: fallbackSize,
      total: 1,
      totalPages: 1,
    },
  };
}
